import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { LanguageService } from '@aston/foundation';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { SubsidiaryStatus } from 'apps/console/src/app/subsidiaries-module/enums';
import { FederationsService } from 'apps/console/src/app/federations-module/services';
import * as RoutesDefinitions from 'apps/console/src/app/routes-definitions';

import { FederationDetailStoreActions } from '../federation-detail-store';
import { AppStoreActions, AppStoreSelectors } from '../app-store';
import * as subsidiariesActions from '../new-account-store/actions';
import * as Routes from '../../routes-definitions';

import * as featureActions from './actions';

@Injectable({providedIn: 'root'})
export class FederationsStoreEffects {
	private lang = inject(LanguageService);
	private actions$ = inject(Actions);
	private store = inject(Store);
	private federationsService = inject(FederationsService);
	private router = inject(Router);


	federationsRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadFederationsRequest, subsidiariesActions.SubsidiaryInitializedChanged),
		withLatestFrom(this.store.select(AppStoreSelectors.selectCurrentUserIsSupport)),
		switchMap(([_, isSupport]) => (isSupport ? // When logged as support, fetch only active subsidiaries
			this.federationsService.getAllFederations() :
			this.federationsService.getFederations()
		).pipe(
			map(allFederations => {
				const federations = allFederations.filter(s => s.status === SubsidiaryStatus.Initialized);
				const hasPendingItems = allFederations.some(s => s.status === SubsidiaryStatus.Registered);
				return featureActions.LoadFederationsSuccess({entity: federations, hasPendingItems});
			}),
			catchError(error => of(featureActions.LoadFederationsFailure({error})))
		))
	));

	getFederationRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadFederationRequest),
		switchMap(action => this.federationsService.getFederation(action.id).pipe(
			switchMap(entity => [
				featureActions.LoadFederationSuccess({ entity, correlationParams: action.correlationParams }),
			]),
			catchError(error => of(featureActions.LoadFederationFailure({ error })))
		))
	))

	createFederationRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CreateFederationRequest),
		switchMap(action => this.federationsService.createFederation(action.entity).pipe(
			switchMap(id => [
				featureActions.CreateFederationSuccess({ entity: { ...action.entity, federationId: id }, correlationParams: action.correlationParams }),
				FederationDetailStoreActions.LoadFederationSuccess({ entity: { ...action.entity, federationId: id } }),
				AppStoreActions.Navigate({ to: [RoutesDefinitions.getFederationValidationFullPath()] })
			]),
			catchError(error => of(featureActions.CreateFederationFailure({ error })))
		))
	))

	updateFederationRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateFederationRequest),
		switchMap(action => this.federationsService.updateFederation(action.entity).pipe(
			switchMap(_ => [
				featureActions.UpdateFederationSuccess({ entity: action.entity, correlationParams: action.correlationParams }),
			]),
			catchError(error => of(featureActions.UpdateFederationFailure({ error })))
		))
	))

	deleteFederationConfirm$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.DeleteFederationConfirm),
		map(action => AppStoreActions.OpenConfirmationModal(
			{textsKey: 'Federation.Confirmations.Delete.'},
			featureActions.DeleteFederationRequest(action.entity.federationId)
		))
	));

	deleteFederationRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.DeleteFederationRequest),
		switchMap(action => this.federationsService.deleteFederation(action.id).pipe(
			switchMap(_ => [
				featureActions.DeleteFederationSuccess({ entity: null }),
			]),
			tap(_ => this.router.navigate([RoutesDefinitions.getFederationsFullPath()])),
			catchError(error => of(featureActions.DeleteFederationFailure({ error })))
		))
	))

	redirectToTenant$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.RedirectTo),
		withLatestFrom(this.lang.language$),
		switchMap(([action, lang]) => this.federationsService.getTenantUrl(action.federationId).pipe(
			map(tenantUrl => tenantUrl.withPath(action.path || Routes.MiddleWelcomeRoutePath).withParams({ lang })),
			tap(tenantUrl => window.location.href = tenantUrl.toString()),
			catchError(_ => of(AppStoreActions.ToastError('Errors.ErrorHasOccurred')))
		))
	), {dispatch: false});

	redirectToTenantNewTab$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.RedirectToNewTab),
		withLatestFrom(this.lang.language$),
		switchMap(([action, lang]) => this.federationsService.getTenantUrl(action.federationId).pipe(
			map(tenantUrl => tenantUrl.withPath(Routes.MiddleWelcomeRoutePath).withParams({ lang })),
			tap(tenantUrl => window.open(tenantUrl.toString(), '_blank')),
			catchError(_ => of(AppStoreActions.ToastError('Errors.ErrorHasOccurred')))
		))
	), {dispatch: false});
}
